import { cn } from '@gramas/ui'
import type { ReactNode } from 'react'

/**
 * A drawn glyph per chain, in the app's own hand.
 *
 * Not the real logos: a wall of eight brand marks, each with its own colour,
 * weight and lozenge, would be the loudest thing in an app that has exactly one
 * accent — and a screenshot of somebody else's trademark is not ours to ship.
 * These are the same hairline stroke as the produce tiles, so the shelf reads as
 * one screen and each chain is still recognisable at a glance: the arches, the
 * bucket, the ring, the cup.
 */
const GLYPHS: Readonly<Record<string, ReactNode>> = {
  mcdonalds: <path d="M4 19v-7.5a4 4 0 018 0 4 4 0 018 0V19M12 11.5V19" />,
  // Two bun halves with the name's gap between them, which is the shape of the
  // lockup itself — a ring with a line through it was indistinguishable from
  // Circle K's at 24 px.
  'burger-king': (
    <>
      <path d="M3.6 11.1a8.4 5.7 0 0116.8 0" />
      <path d="M3.6 12.9a8.4 5.7 0 0016.8 0" />
    </>
  ),
  kfc: (
    <>
      <ellipse cx="12" cy="7.4" rx="7.6" ry="2" />
      <path d="M4.4 7.4l1.6 10.2A2 2 0 008 19.4h8a2 2 0 002-1.8l1.6-10.2" />
      <path d="M5.6 13.4h12.8" />
    </>
  ),
  hesburger: (
    <>
      <path d="M4.5 10.4a7.5 4.6 0 0115 0z" />
      <path d="M4.5 13.2h15" />
      <path d="M5 15.6h14a2.4 2.4 0 01-2.4 2.4H7.4A2.4 2.4 0 015 15.6z" />
    </>
  ),
  'circle-k': (
    <>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M9.8 7.8v8.4M14.6 7.8L9.8 12l4.8 4.2" />
    </>
  ),
  viada: <path d="M12 3.6c4.2 5.4 6.2 8.2 6.2 11.1a6.2 6.2 0 11-12.4 0c0-2.9 2-5.7 6.2-11.1z" />,
  caffeine: (
    <>
      <path d="M5 5.8h14l-1.2 2.4H6.2z" />
      <path d="M6.6 8.2l1.2 9.6a1.8 1.8 0 001.8 1.6h4.8a1.8 1.8 0 001.8-1.6l1.2-9.6" />
    </>
  ),
  'vero-cafe': (
    <>
      <path d="M4.8 6.8h10.4v5.6a5.2 5.2 0 01-10.4 0z" />
      <path d="M15.2 8.2h1.6a2.4 2.4 0 010 4.8h-1.6" />
      <path d="M3.4 18.6h14.2" />
    </>
  ),
  narvesen: (
    <>
      <path d="M4 14a3.2 3.2 0 013.2-3.2h9.6a3.2 3.2 0 010 6.4H7.2A3.2 3.2 0 014 14z" />
      <path d="M7.6 13.2c.9.9 1.8.9 2.7 0s1.8-.9 2.7 0 1.8.9 2.7 0" />
    </>
  ),
}

/** Anything imported later still gets a tile rather than an empty square. */
const FALLBACK: ReactNode = (
  <>
    <path d="M6.4 8h11.2l-1.2 10.4a1.6 1.6 0 01-1.6 1.4H9.2a1.6 1.6 0 01-1.6-1.4z" />
    <path d="M9.2 8V6.4a2.8 2.8 0 015.6 0V8" />
  </>
)

export interface ChainGridProps {
  chains: ReadonlyArray<{ id: number; slug: string; name: string }>
  value: string | null
  onChange: (slug: string) => void
  className?: string
}

/**
 * Four across, because eight chains then fit in two rows without scrolling and
 * every name still gets two lines to itself — "Burger King" truncated to
 * "Burger K…" is a worse tile than a slightly smaller one.
 */
export function ChainGrid({ chains, value, onChange, className }: ChainGridProps) {
  return (
    <div className={cn('grid grid-cols-4 gap-1.5 px-3 pb-2', className)}>
      {chains.map((chain) => {
        const active = chain.slug === value
        return (
          <button
            key={chain.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(chain.slug)}
            className={cn(
              'flex select-none flex-col items-center gap-1 rounded-[var(--radius-card)] border px-1 py-2.5',
              'transition-colors duration-[140ms]',
              active
                ? 'border-accent bg-accent/10 text-accent-text'
                : 'border-line text-dim active:border-line-strong active:text-ink',
            )}
          >
            <svg
              viewBox="0 0 24 24"
              className="size-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              {GLYPHS[chain.slug] ?? FALLBACK}
            </svg>
            <span className="text-center text-[0.6875rem] leading-tight">{chain.name}</span>
          </button>
        )
      })}
    </div>
  )
}
